import type {
  ConfigurationResourceApplicationItemBffV2Api,
  ConfigurationResourceOsVersionBffV2Api,
  ConfigurationResourceReadBffV2Api,
  ConfigurationResourceSnapshotApplicationItemDeploymentBffV2Api,
  ConfigurationResourceSnapshotSystemPackageItemDeploymentBffV2Api,
  ConfigurationResourceSystemPackageItemBffV2Api,
  GetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import type {
  ConfigurationResourceApplicationVersionFront,
  ConfigurationResourceOsVersionFront,
  ConfigurationResourceReadFront,
  ConfigurationResourceRemoteKeyInjectionFront,
  ConfigurationResourceSystemPackageVersionFront,
} from '@ingenico/react-configuration-generator';
import { toString } from 'lodash-es';

export function mapGetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApiToConfigurationResourceFront(
  response: GetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApi,
): ConfigurationResourceReadFront {
  return {
    remoteKeyInjection: mapConfigurationResourceRemoteKeyInjectionApiToConfigurationResourceRemoteKeyInjectionFront(
      response.remoteKeyInjection?.map((name) => ({ name, id: name })),
    ),
    selectedApplicationVersions:
      mapDeployedComputedConfigurationResourceApplicationVersionApiToConfigurationResourceApplicationVersionFront(
        response.applicationVersions,
      ),
    selectedOsVersion: mapConfigurationResourceOsVersionApiToConfigurationResourceOsVersionFront(response.osVersion),
    selectedSystemPackageVersions:
      mapDeployedComputedConfigurationResourceSystemPackageVersionApiToConfigurationResourceSystemPackageVersionFront(
        response.systemPackageVersions,
      ),
  };
}

export function mapConfigurationResourceApiToConfigurationResourceFront(
  response: Pick<ConfigurationResourceReadBffV2Api, 'definition' | 'id'>,
): ConfigurationResourceReadFront {
  return {
    id: response.id,
    remoteKeyInjection: mapConfigurationResourceRemoteKeyInjectionApiToConfigurationResourceRemoteKeyInjectionFront(
      response.definition?.configuration?.remoteKeyInjection?.map((name) => ({ name, id: name })),
    ),
    selectedApplicationVersions:
      mapConfigurationResourceApplicationVersionApiToConfigurationResourceApplicationVersionFront(
        response.definition?.configuration?.applicationVersions,
      ),
    selectedOsVersion: mapConfigurationResourceOsVersionApiToConfigurationResourceOsVersionFront(
      response.definition?.configuration?.osVersion,
    ),
    selectedSystemPackageVersions:
      mapConfigurationResourceSystemPackageVersionApiToConfigurationResourceSystemPackageVersionFront(
        response.definition?.configuration?.systemPackageVersions,
      ),
  };
}

export function mapConfigurationResourceOsVersionApiToConfigurationResourceOsVersionFront(
  data?: ConfigurationResourceOsVersionBffV2Api,
): ConfigurationResourceOsVersionFront | undefined {
  return (
    data && {
      id: data.id,
      osId: data.osId,
      osName: data.osName,
      osVersionId: data.osVersionId,
      osLogo: data.osLogo,
      osVersionNumber: data.osVersionNumber,
      isDeprecated: data.osVersionStatus === 'DEPRECATED',
    }
  );
}

export function mapDeployedComputedConfigurationResourceApplicationVersionApiToConfigurationResourceApplicationVersionFront(
  data: ConfigurationResourceSnapshotApplicationItemDeploymentBffV2Api[] | undefined,
): ConfigurationResourceApplicationVersionFront[] {
  return (
    data?.map((app: ConfigurationResourceSnapshotApplicationItemDeploymentBffV2Api) => ({
      applicationId: app.applicationId,
      applicationLogo: app.applicationLogo,
      applicationName: app.applicationName,
      applicationVersionId: toString(app.applicationVersionId), // prop in bff should be required
      applicationVersionNumber: toString(app.applicationVersionNumber), // prop in bff should be required
      configurationModuleName: toString(app.configurationModuleName), // prop in bff should be required
      configurationModuleSnapshotId: app.configurationModuleSnapshotId,
      deploymentUnitId: toString(app.deploymentUnitId), // prop in bff should be required (?)
      id: app.id,
      isCurrentVersionDeprecated: app.applicationVersionStatus === 'DEPRECATED',
      isDeprecated: app.applicationStatus === 'DEPRECATED',
      isSnapshot: true,
      order: app.order,
      type: app.type,
    })) ?? []
  );
}

export function mapConfigurationResourceApplicationVersionApiToConfigurationResourceApplicationVersionFront(
  data: ConfigurationResourceApplicationItemBffV2Api[] | undefined,
): ConfigurationResourceApplicationVersionFront[] {
  return (
    data?.map((app: ConfigurationResourceApplicationItemBffV2Api) => ({
      id: app.id,
      type: app.type,
      order: app.order,
      applicationId: app.applicationId,
      applicationLogo: app.applicationLogo,
      applicationName: app.applicationName,
      applicationVersionId: toString(app.applicationVersionId), // prop in bff should be required
      applicationVersionNumber: toString(app.applicationVersionNumber), // prop in bff should be required
      configurationModuleName: toString(app.configurationModuleName), // prop in bff should be required
      isDeprecated: app.applicationStatus === 'DEPRECATED',
      isSnapshot: false,
      isCurrentVersionDeprecated: app.applicationVersionStatus === 'DEPRECATED',
    })) ?? []
  );
}

export function mapDeployedComputedConfigurationResourceSystemPackageVersionApiToConfigurationResourceSystemPackageVersionFront(
  data: ConfigurationResourceSnapshotSystemPackageItemDeploymentBffV2Api[] | undefined,
): ConfigurationResourceSystemPackageVersionFront[] {
  return (
    data?.map((sp: ConfigurationResourceSnapshotSystemPackageItemDeploymentBffV2Api) => ({
      configurationModuleName: toString(sp.configurationModuleName), // prop in bff should be required
      configurationModuleSnapshotId: sp.configurationModuleSnapshotId,
      deploymentUnitId: toString(sp.deploymentUnitId),
      id: sp.id,
      isCurrentVersionDeprecated: sp.systemPackageVersionStatus === 'DEPRECATED',
      isDeprecated: sp.systemPackageStatus === 'DEPRECATED',
      isSnapshot: true,
      order: sp.order,
      systemPackageId: sp.systemPackageId,
      systemPackageLogo: sp.systemPackageLogo,
      systemPackageName: sp.systemPackageName,
      systemPackageVersionId: toString(sp.systemPackageVersionId), // prop in bff should be required
      systemPackageVersionNumber: toString(sp.systemPackageVersionNumber), // prop in bff should be required
      type: sp.type,
    })) ?? []
  );
}

export function mapConfigurationResourceSystemPackageVersionApiToConfigurationResourceSystemPackageVersionFront(
  data: ConfigurationResourceSystemPackageItemBffV2Api[] | undefined,
): ConfigurationResourceSystemPackageVersionFront[] {
  return (
    data?.map((sp: ConfigurationResourceSystemPackageItemBffV2Api) => ({
      id: sp.id,
      type: sp.type,
      order: sp.order,
      systemPackageLogo: sp.systemPackageLogo,
      systemPackageName: sp.systemPackageName,
      systemPackageVersionId: toString(sp.systemPackageVersionId), // prop in bff should be required
      systemPackageVersionNumber: toString(sp.systemPackageVersionNumber), // prop in bff should be required
      systemPackageId: sp.systemPackageId,
      configurationModuleName: toString(sp.configurationModuleName), // prop in bff should be required
      isDeprecated: sp.systemPackageStatus === 'DEPRECATED',
      isSnapshot: false,
      isCurrentVersionDeprecated: sp.systemPackageVersionStatus === 'DEPRECATED',
    })) ?? []
  );
}

export function mapConfigurationResourceRemoteKeyInjectionApiToConfigurationResourceRemoteKeyInjectionFront(
  data: ConfigurationResourceRemoteKeyInjectionFront[] | undefined,
): string[] {
  return data?.map((rki) => rki.name) ?? [];
}
